import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input } from '@mlm/ui';
import type { ActiveSlot } from '../../lib/api';
import { SlotBalances } from './slot-balances';

/** Slots per page in the picker. */
const SLOT_PAGE_SIZE = 10;

/**
 * Slot picker for a member who owns more than one.
 *
 * Paged at ten with a filter box: stacking members routinely hold dozens of
 * slots, and `DialogContent` sets no height cap, so the un-paged list used to
 * run off the bottom of the viewport with no way to reach the entries below.
 * Filtering + paging are client-side because the whole active-slot list is
 * already in hand (the auto-pick rule needs the true count anyway).
 */
export function SlotPickerDialog({
  open,
  slots,
  memberLabel,
  showGc,
  showCoin,
  onPick,
  onClose,
}: {
  open: boolean;
  slots: ActiveSlot[];
  memberLabel: string;
  showGc: boolean;
  showCoin: boolean;
  onPick: (s: ActiveSlot) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const term = q.trim().toLowerCase();
  const filtered = term
    ? slots.filter(
        (s) =>
          s.slotCode.toLowerCase().includes(term) || s.membershipName.toLowerCase().includes(term),
      )
    : slots;

  const pageCount = Math.max(1, Math.ceil(filtered.length / SLOT_PAGE_SIZE));
  // Typing a filter can shrink the list under the current page; clamp on render
  // rather than in an effect so there is never a frame showing an empty page.
  const current = Math.min(page, pageCount);
  const visible = filtered.slice((current - 1) * SLOT_PAGE_SIZE, current * SLOT_PAGE_SIZE);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pick a slot</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {memberLabel} has {slots.length} active {slots.length === 1 ? 'slot' : 'slots'}. Choose
          the one to use for this sale.
        </p>
        {slots.length > SLOT_PAGE_SIZE && (
          <Input
            autoFocus
            placeholder="Filter by slot code or tier…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        )}
        {visible.length === 0 ? (
          <p className="rounded-md border px-3 py-6 text-center text-sm text-muted-foreground">
            No slot matches “{q.trim()}”.
          </p>
        ) : (
          <ul className="max-h-[50vh] divide-y overflow-y-auto rounded-md border">
            {visible.map((s) => (
              <li key={s.id}>
                <button
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-accent"
                  onClick={() => onPick(s)}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-sm">{s.slotCode}</div>
                    <div className="text-xs text-muted-foreground">{s.membershipName}</div>
                    {/* Balances per row: with several slots the cashier is often
                        choosing the one that can actually cover a scrip tender. */}
                    <SlotBalances slot={s} showGc={showGc} showCoin={showCoin} />
                  </div>
                  {s.activatedAt && (
                    <span className="flex-none text-xs text-muted-foreground">
                      activated {new Date(s.activatedAt).toLocaleDateString()}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {pageCount > 1 && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Page {current} of {pageCount} · {filtered.length}{' '}
              {filtered.length === 1 ? 'slot' : 'slots'}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={current <= 1}
                onClick={() => setPage(current - 1)}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={current >= pageCount}
                onClick={() => setPage(current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
